<template>
  <div class="product-import-modal">
    <nav class="product-import-modal__progress" :aria-label="$t('products.import.steps_label')">
      <ol>
        <li
          v-for="item in stepItems"
          :key="item.id"
          :aria-current="step === item.id ? 'step' : null"
        >
          <button type="button" :disabled="!canOpenStep(item.id)" @click="goToStep(item.id)">
            {{ item.label }}
          </button>
        </li>
      </ol>
    </nav>

    <div
      v-if="step === 1"
      class="product-import-modal__intake"
      :class="{ 'product-import-modal__intake--active': dragging }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <Btn
        variant="primary"
        icon="upload"
        type="button"
        :text="$t('products.import.choose_file')"
        @click.stop="triggerFileInput"
      />
      <p class="product-import-modal__hint">{{ $t('products.import.drop_hint') }}</p>
      <button type="button" class="product-import-modal__link" @click.stop="downloadTemplate">
        {{ $t('products.import.download_template') }}
      </button>
      <p v-if="parseError" class="product-import-modal__error" role="alert">{{ parseError }}</p>
      <input
        ref="fileInput"
        type="file"
        accept=".csv,.xlsx,.xls"
        class="product-import-modal__file-input"
        @change="onFileChange"
      />
    </div>

    <div v-else-if="step === 2" class="product-import-modal__panel">
      <div class="product-import-modal__filebar">
        <div>
          <p class="product-import-modal__filename">{{ fileName }}</p>
          <p class="product-import-modal__hint">
            {{ $t('products.import.row_count', { n: rows.length }) }}
            ·
            {{ $t('products.import.mapped_count', { mapped: mappedCount, total: headers.length }) }}
          </p>
        </div>
        <Btn variant="plain" :text="$t('products.import.change_file')" @click="triggerFileInput" />
      </div>

      <p v-if="!canPreview" class="product-import-modal__error" role="alert">
        {{ $t('products.import.error_title_required') }}
      </p>
      <p v-else-if="mappingError" class="product-import-modal__error" role="alert">
        {{ mappingError }}
      </p>

      <ul class="product-import-modal__map" role="list">
        <li v-for="header in headers" :key="header" class="product-import-modal__map-row">
          <div class="product-import-modal__map-source">
            <span class="product-import-modal__column">{{ header }}</span>
            <span class="product-import-modal__samples">
              {{ sampleValues(rows, header).join(', ') || $t('products.import.no_sample') }}
            </span>
          </div>
          <label class="product-import-modal__map-field">
            <span class="visually-hidden">
              {{ $t('products.import.map_column', { column: header }) }}
            </span>
            <select
              class="product-import-modal__select"
              :value="mapping[header] || ''"
              @change="setMapping(header, $event.target.value)"
            >
              <option v-for="option in fieldOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
        </li>
      </ul>

      <details class="product-import-modal__advanced">
        <summary>{{ $t('products.import.existing_products') }}</summary>
        <div class="product-import-modal__advanced-body">
          <SelectInput
            id="import-match-by"
            :label="$t('products.import.match_by')"
            v-model="matchBy"
            :options="matchByOptions"
          />
          <SelectInput
            v-if="matchBy !== 'none'"
            id="import-on-duplicate"
            :label="$t('products.import.on_duplicate')"
            v-model="onDuplicate"
            :options="onDuplicateOptions"
          />
          <CheckboxInput
            id="import-create-categories"
            :label="$t('products.import.create_missing_categories')"
            v-model="createMissingCategories"
          />
        </div>
      </details>
    </div>

    <div v-else class="product-import-modal__panel">
      <div v-if="previewLoading" class="product-import-modal__loading">
        <LoadingSpinner size="sm" />
        <span>{{ $t('products.import.preview_loading') }}</span>
      </div>

      <template v-else-if="result">
        <p class="product-import-modal__summary">
          {{ resultSummary }}
        </p>
        <p v-if="result.error_count" class="product-import-modal__error" role="alert">
          {{ $t('products.import.summary_errors', { n: result.error_count }) }}
        </p>

        <div class="product-import-modal__table-wrap">
          <table class="product-import-modal__table">
            <thead>
              <tr>
                <th>{{ $t('products.import.row') }}</th>
                <th>{{ $t('products.import.fields.title') }}</th>
                <th>{{ $t('products.import.action') }}</th>
                <th>{{ $t('products.import.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in visiblePreview" :key="item.row">
                <td>{{ item.row }}</td>
                <td class="product-import-modal__title-cell">{{ item.title || '—' }}</td>
                <td>
                  <Label :variant="actionVariant(item.action)" size="sm">
                    {{ $t(`products.import.actions.${item.action}`) }}
                  </Label>
                </td>
                <td>{{ item.error || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="result.preview.length > visiblePreview.length" class="product-import-modal__hint">
          {{ $t('products.import.preview_truncated', { n: result.preview.length }) }}
        </p>
      </template>

      <p v-if="importError" class="product-import-modal__error" role="alert">{{ importError }}</p>
    </div>

    <div class="modal__actions">
      <Btn
        v-if="step > 1 && !imported"
        :text="$t('products.import.back')"
        variant="outlined"
        :disabled="loading"
        @click="goBack"
      />
      <Btn
        v-if="step === 2"
        :text="$t('products.import.preview')"
        variant="primary"
        :disabled="!canPreview || loading"
        @click="runPreview"
      />
      <Btn
        v-else-if="step === 3 && !imported"
        :text="$t('products.import.confirm')"
        variant="primary"
        :loading="loading"
        :disabled="loading || !result || result.error_count === (result.preview || []).length"
        @click="runImport"
      />
      <Btn
        v-else-if="imported"
        :text="$t('products.import.close')"
        variant="primary"
        @click="handleClose"
      />
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';
  import { useUIStore } from '@/store/uiStore';
  import { importProducts } from '@shared/services/productService';
  import { parseSpreadsheetFile } from '@/utils/spreadsheetParse';
  import {
    IMPORTABLE_FIELDS,
    suggestProductImportMapping,
    mappingHasTitle,
    duplicateMappedFields,
    sampleValues,
    buildTemplateCsv,
  } from '@/utils/productImportMapping';

  export default {
    name: 'ProductImportModal',
    components: {
      Btn,
      Label,
      LoadingSpinner,
      SelectInput,
      CheckboxInput,
    },
    props: {
      modalId: { type: [Number, String], required: true },
      feedId: { type: String, required: true },
      onImported: { type: Function, default: null },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();

      const step = ref(1);
      const fileInput = ref(null);
      const dragging = ref(false);
      const dragDepth = ref(0);
      const fileName = ref('');
      const headers = ref([]);
      const rows = ref([]);
      const mapping = ref({});
      const matchBy = ref('base_sku');
      const onDuplicate = ref('skip');
      const createMissingCategories = ref(true);
      const parseError = ref('');
      const mappingError = ref('');
      const importError = ref('');
      const previewLoading = ref(false);
      const loading = ref(false);
      const result = ref(null);
      const imported = ref(false);

      watch(loading, (value) => {
        uiStore.updateModalProps(props.modalId, { closable: !value });
      });

      const stepItems = computed(() => [
        { id: 1, label: $t('products.import.step_upload') },
        { id: 2, label: $t('products.import.step_map') },
        { id: 3, label: $t('products.import.step_preview') },
      ]);

      const fieldOptions = computed(() => [
        { value: '', label: $t('products.import.skip_column') },
        ...IMPORTABLE_FIELDS.map((field) => ({
          value: field.key,
          label: field.required
            ? `${$t(`products.import.fields.${field.key}`)} *`
            : $t(`products.import.fields.${field.key}`),
        })),
      ]);

      const matchByOptions = computed(() => [
        { value: 'none', label: $t('products.import.match_none') },
        { value: 'base_sku', label: $t('products.import.match_sku') },
        { value: 'title', label: $t('products.import.match_title') },
      ]);

      const onDuplicateOptions = computed(() => [
        { value: 'skip', label: $t('products.import.duplicate_skip') },
        { value: 'update', label: $t('products.import.duplicate_update') },
        { value: 'create', label: $t('products.import.duplicate_create') },
      ]);

      const canPreview = computed(() => mappingHasTitle(mapping.value));
      const mappedCount = computed(() => Object.values(mapping.value).filter(Boolean).length);
      const visiblePreview = computed(() => (result.value?.preview || []).slice(0, 50));

      const resultSummary = computed(() => {
        if (!result.value) return '';
        if (imported.value) {
          return $t('products.import.summary_done', {
            created: result.value.created_count,
            updated: result.value.updated_count,
            skipped: result.value.skipped_count,
          });
        }
        return $t('products.import.summary', {
          created: result.value.created_count,
          updated: result.value.updated_count,
          skipped: result.value.skipped_count,
        });
      });

      const canOpenStep = (id) => {
        if (imported.value) return false;
        if (id === 1) return true;
        if (id === 2) return rows.value.length > 0;
        return false;
      };

      const goToStep = (id) => {
        if (!canOpenStep(id)) return;
        if (id < 3) {
          result.value = null;
          importError.value = '';
        }
        step.value = id;
      };

      const setMapping = (header, value) => {
        mapping.value = { ...mapping.value, [header]: value };
        mappingError.value = '';
      };

      const triggerFileInput = () => {
        fileInput.value?.click();
      };

      const handleParseError = (error) => {
        const code = error?.message;
        if (code === 'too_large') parseError.value = $t('products.import.error_too_large');
        else if (code === 'unsupported') parseError.value = $t('products.import.error_unsupported');
        else if (code === 'empty') parseError.value = $t('products.import.error_empty');
        else parseError.value = $t('products.import.error_parse');
      };

      const loadParsed = ({ headers: nextHeaders, rows: nextRows, filename }) => {
        headers.value = nextHeaders;
        rows.value = nextRows;
        fileName.value = filename;
        mapping.value = suggestProductImportMapping(nextHeaders);
        parseError.value = '';
        mappingError.value = '';
        result.value = null;
        imported.value = false;
        step.value = 2;
      };

      const onFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
          loadParsed(await parseSpreadsheetFile(file));
        } catch (error) {
          handleParseError(error);
        }
      };

      const onDragEnter = () => {
        dragDepth.value += 1;
        dragging.value = true;
      };

      const onDragLeave = () => {
        dragDepth.value = Math.max(0, dragDepth.value - 1);
        if (dragDepth.value === 0) dragging.value = false;
      };

      const onDrop = async (event) => {
        dragDepth.value = 0;
        dragging.value = false;
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;
        try {
          loadParsed(await parseSpreadsheetFile(file));
        } catch (error) {
          handleParseError(error);
        }
      };

      const downloadTemplate = () => {
        const blob = new Blob([buildTemplateCsv()], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'product-import-template.csv';
        link.click();
        URL.revokeObjectURL(url);
      };

      const payload = (dryRun) => ({
        mapping: mapping.value,
        rows: rows.value,
        match_by: matchBy.value,
        on_duplicate: matchBy.value === 'none' ? 'create' : onDuplicate.value,
        create_missing_categories: createMissingCategories.value,
        dry_run: dryRun,
      });

      const validateMappingState = () => {
        if (!mappingHasTitle(mapping.value)) {
          mappingError.value = $t('products.import.error_title_required');
          return false;
        }
        const duplicates = duplicateMappedFields(mapping.value);
        if (duplicates.length) {
          mappingError.value = $t('products.import.error_duplicate_fields');
          return false;
        }
        return true;
      };

      const runPreview = async () => {
        if (!validateMappingState()) return;
        step.value = 3;
        previewLoading.value = true;
        importError.value = '';
        result.value = null;
        try {
          result.value = await importProducts(props.feedId, payload(true));
        } catch (error) {
          importError.value = error.response?.data?.error || $t('products.import.error_preview');
        } finally {
          previewLoading.value = false;
        }
      };

      const runImport = async () => {
        if (loading.value) return;
        loading.value = true;
        importError.value = '';
        try {
          result.value = await importProducts(props.feedId, payload(false));
          imported.value = true;
          if (props.onImported) {
            await props.onImported(result.value);
          }
        } catch (error) {
          importError.value = error.response?.data?.error || $t('products.import.error_import');
        } finally {
          loading.value = false;
        }
      };

      const goBack = () => {
        goToStep(step.value === 3 ? 2 : 1);
      };

      const handleClose = () => {
        uiStore.closeModal(props.modalId);
      };

      const actionVariant = (action) => {
        if (action === 'create') return 'success';
        if (action === 'update') return 'info';
        if (action === 'error') return 'danger';
        return 'default';
      };

      return {
        step,
        stepItems,
        fileInput,
        dragging,
        fileName,
        headers,
        rows,
        mapping,
        matchBy,
        onDuplicate,
        createMissingCategories,
        parseError,
        mappingError,
        importError,
        previewLoading,
        loading,
        result,
        imported,
        fieldOptions,
        matchByOptions,
        onDuplicateOptions,
        canPreview,
        mappedCount,
        visiblePreview,
        resultSummary,
        sampleValues,
        canOpenStep,
        goToStep,
        setMapping,
        triggerFileInput,
        onFileChange,
        onDragEnter,
        onDragLeave,
        onDrop,
        downloadTemplate,
        runPreview,
        runImport,
        goBack,
        handleClose,
        actionVariant,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .product-import-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__progress {
      ol {
        display: flex;
        gap: 0.25rem;
        list-style: none;
        margin: 0;
        padding: 0;
      }

      li:not(:last-child)::after {
        content: '·';
        margin: 0 0.5rem 0 0.25rem;
        color: var(--slate-300);
      }

      button {
        border: 0;
        background: none;
        padding: 0.25rem 0;
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--slate-400);
        min-height: 44px;
      }

      button:disabled {
        cursor: default;
      }

      [aria-current='step'] button {
        color: var(--slate-900);
        font-weight: 600;
      }

      @include hover {
        button:not(:disabled) {
          color: var(--slate-700);
        }
      }
    }

    &__intake {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 1.5rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
      transition: border-color 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    &__intake--active {
      border-color: var(--brand-500);
      background: var(--brand-50);
    }

    &__panel {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__filebar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    &__filename {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__hint {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__link {
      background: none;
      border: 0;
      padding: 0;
      min-height: 44px;
      color: var(--brand-700);
      font-size: 0.8125rem;
      cursor: pointer;
      @include hover {
        color: var(--brand-800);
      }
    }

    &__file-input {
      display: none;
    }

    &__map {
      list-style: none;
      margin: 0;
      padding: 0;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      overflow: auto;
      max-height: 280px;
    }

    &__map-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(11rem, 14rem);
      gap: 0.75rem;
      align-items: center;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--slate-200);

      &:last-child {
        border-bottom: 0;
      }
    }

    &__map-source {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__column {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__samples {
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &__map-field {
      margin: 0;
    }

    &__select {
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--white);
      font-size: 0.875rem;
      font-family: inherit;
      color: var(--slate-900);

      &:hover {
        border-color: var(--slate-300);
      }

      &:focus {
        outline: 2px solid var(--brand-500);
        outline-offset: 1px;
      }
    }

    &__advanced {
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      padding: 0.75rem 1rem;

      summary {
        cursor: pointer;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--slate-800);
        min-height: 32px;
      }
    }

    &__advanced-body {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }

    &__error {
      margin: 0;
      color: var(--red-700);
      font-size: 0.8125rem;
    }

    &__summary {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__table-wrap {
      overflow: auto;
      max-height: 280px;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
    }

    &__table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8125rem;

      th,
      td {
        padding: 0.625rem 0.75rem;
        text-align: left;
        border-bottom: 1px solid var(--slate-200);
        vertical-align: middle;
      }

      th {
        background: var(--slate-50);
        font-weight: 600;
        color: var(--slate-600);
        position: sticky;
        top: 0;
      }
    }

    &__title-cell {
      @include ellipsis(1);
      max-width: 16rem;
    }

    &__loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem 0;
      color: var(--slate-500);
      font-size: 0.875rem;
    }

    .modal__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 0.25rem;
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
    }

    @include lg-down {
      &__map-row {
        grid-template-columns: 1fr;
        align-items: stretch;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      &__intake {
        transition: none;
      }
    }
  }
</style>
